import { fetchWithRetry as fetch, responseError } from './apiError';
import { tokenService } from './tokenService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const toApiAssetUrl = (url?: string | null): string | null => {
  if (!url) return null;
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  if (url.startsWith('/')) return `${API_URL}${url}`;

  return url;
};

export type ProjectRole = 'owner' | 'admin' | 'writer' | 'reader';
export type ProjectTeamRole = 'manager' | 'developer';

export interface TeamMember {
  id: string;
  user_id: string;
  email: string;
  name: string;
  avatar_url?: string | null;
  role: ProjectRole;
  team_role: ProjectTeamRole;
  manager_user_id?: string | null;
  manager_name?: string | null;
  is_current_user?: boolean;
  ai_credit_quota?: number | null;
  ai_credits_used?: number;
  ai_credits_remaining?: number | null;
  ai_credit_quota_allocated?: number;
  ai_credit_quota_exhausted?: boolean;
  quota_required?: boolean;
  can_manage_ai_credit_quota?: boolean;
  is_unlimited?: boolean;
  unlimited_scope?: 'personal' | 'organization' | null;
  created_at?: string;
}

export interface TaskAssignee {
  id: string;
  user_id: string;
  name: string;
  avatar_url?: string | null;
  role: ProjectRole;
  team_role: ProjectTeamRole;
}

export interface Invitation {
  id: string;
  email: string;
  role: ProjectRole;
  team_role: ProjectTeamRole;
  manager_user_id?: string | null;
  manager_name?: string | null;
  invited_by: string;
  created_at?: string;
  expires_at?: string;
  is_expired: boolean;
}

interface TeamResponse {
  members: TeamMember[];
  invitations: Invitation[];
}

export interface InvitationDetails {
  id: string;
  email: string;
  role: ProjectRole;
  team_role: ProjectTeamRole;
  status: string;
  is_expired: boolean;
  project_name: string;
  inviter_name: string;
  expires_at?: string;
}

export interface TeamAnalyticsProjectOption {
  id: string;
  title: string;
  role: ProjectRole;
  is_owner: boolean;
  analytics_scope: 'all' | 'reports' | 'self';
}

export interface TeamAnalyticsUserRow {
  project_id: string;
  project_title: string;
  user_id: string;
  name: string;
  email: string;
  avatar_url?: string | null;
  role: ProjectRole;
  team_role: ProjectTeamRole;
  manager_user_id?: string | null;
  manager_name?: string | null;
  session_count: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  feedback_count: number;
  avg_user_effort_score?: number | null;
  avg_technical_depth_score?: number | null;
}

export interface TeamAnalyticsTotals {
  project_count: number;
  user_count: number;
  session_count: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  feedback_count: number;
  avg_user_effort_score?: number | null;
  avg_technical_depth_score?: number | null;
}

export interface TeamAnalyticsHistoryPoint {
  date: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  feedback_count: number;
  avg_user_effort_score?: number | null;
  avg_technical_depth_score?: number | null;
}

export interface TeamAnalyticsResponse {
  projects: TeamAnalyticsProjectOption[];
  selected_project_id?: string | null;
  rows: TeamAnalyticsUserRow[];
  totals: TeamAnalyticsTotals;
  history: TeamAnalyticsHistoryPoint[];
}

export interface SessionGrowthFeedback {
  id: string;
  project_id: string;
  project_title: string;
  session_id: string;
  session_name?: string | null;
  user_id: string;
  name: string;
  email: string;
  avatar_url?: string | null;
  trigger_message_count: number;
  summary: string;
  feedback: string;
  user_effort_score: number;
  technical_depth_score: number;
  created_at: string;
}

export interface GrowthFeedbackTotals {
  feedback_count: number;
  avg_user_effort_score?: number | null;
  avg_technical_depth_score?: number | null;
}

export interface GrowthFeedbackResponse {
  projects: TeamAnalyticsProjectOption[];
  selected_project_id?: string | null;
  feedback: SessionGrowthFeedback[];
  totals: GrowthFeedbackTotals;
  history: TeamAnalyticsHistoryPoint[];
}

export interface AiCreditQuotaStatus {
  project_id: string;
  user_id: string;
  manager_user_id?: string | null;
  manager_name?: string | null;
  ai_credit_quota?: number | null;
  ai_credits_used: number;
  ai_credits_remaining?: number | null;
  ai_credit_quota_allocated: number;
  ai_credit_quota_exhausted: boolean;
  quota_required?: boolean;
  can_manage_ai_credit_quota?: boolean;
  is_unlimited?: boolean;
  unlimited_scope?: 'personal' | 'organization' | null;
}

export const roleLabels: Record<ProjectRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  writer: 'Writer',
  reader: 'Reader',
};

export const teamRoleLabels: Record<ProjectTeamRole, string> = {
  manager: 'Manager',
  developer: 'Developer',
};

async function fetchWithAuth(url: string, options?: globalThis.RequestInit): Promise<globalThis.Response> {
  const token = tokenService.getAccessToken();

  return await fetch(url, {
    ...options,
    headers: {
      ...options?.headers,
      Authorization: `Bearer ${token}`,
    },
  });
}

async function handleJsonResponse<T>(response: globalThis.Response, _errorMsg: string): Promise<T> {
  if (!response.ok) throw await responseError(response);
  return response.json();
}

export const teamService = {
  getTokenAnalytics: async (projectId?: string | null): Promise<TeamAnalyticsResponse> => {
    const params = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
    const response = await fetchWithAuth(`${API_URL}/api/v1/projects/team/analytics${params}`);
    const data = await handleJsonResponse<TeamAnalyticsResponse>(response, 'Failed to fetch team analytics');

    return {
      ...data,
      rows: data.rows.map((row) => ({
        ...row,
        avatar_url: toApiAssetUrl(row.avatar_url),
      })),
    };
  },

  getGrowthFeedback: async (projectId?: string | null): Promise<GrowthFeedbackResponse> => {
    const params = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
    const response = await fetchWithAuth(`${API_URL}/api/v1/projects/growth-feedback${params}`);
    const data = await handleJsonResponse<GrowthFeedbackResponse>(response, 'Failed to fetch growth feedback');

    return {
      ...data,
      feedback: data.feedback.map((item) => ({
        ...item,
        avatar_url: toApiAssetUrl(item.avatar_url),
      })),
    };
  },

  getTeam: async (projectId: string): Promise<TeamResponse> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/projects/${projectId}/team`);
    const data = await handleJsonResponse<TeamResponse>(response, 'Failed to fetch team');

    return {
      ...data,
      members: data.members.map((member) => ({
        ...member,
        avatar_url: toApiAssetUrl(member.avatar_url),
      })),
    };
  },

  getTaskAssignees: async (projectId: string): Promise<TaskAssignee[]> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/projects/${projectId}/task-assignees`);
    const assignees = await handleJsonResponse<TaskAssignee[]>(response, 'Failed to fetch task assignees');

    return assignees.map((assignee) => ({
      ...assignee,
      avatar_url: toApiAssetUrl(assignee.avatar_url),
    }));
  },

  getMyAiCreditQuota: async (projectId: string): Promise<AiCreditQuotaStatus> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/projects/${projectId}/team/ai-credit-quota`);

    return handleJsonResponse(response, 'Failed to fetch AI credit quota');
  },

  inviteMember: async (
    projectId: string,
    email: string,
    role: ProjectRole,
    teamRole: ProjectTeamRole = 'developer',
    managerUserId?: string | null
  ): Promise<Invitation> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/projects/${projectId}/team/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, role, team_role: teamRole, manager_user_id: managerUserId || null }),
    });

    return handleJsonResponse(response, 'Failed to send invitation');
  },

  updateMember: async (
    projectId: string,
    userId: string,
    patch: {
      role?: ProjectRole;
      team_role?: ProjectTeamRole;
      manager_user_id?: string | null;
    }
  ): Promise<TeamMember> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/projects/${projectId}/team/${userId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });

    return handleJsonResponse(response, 'Failed to update role');
  },

  updateMemberRole: async (
    projectId: string,
    userId: string,
    role: ProjectRole
  ): Promise<TeamMember> => teamService.updateMember(projectId, userId, { role }),

  updateMemberAiCreditQuota: async (
    projectId: string,
    userId: string,
    quota: number | null
  ): Promise<TeamMember> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/projects/${projectId}/team/${userId}/ai-credit-quota`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ai_credit_quota: quota }),
    });

    return handleJsonResponse(response, 'Failed to update AI credit quota');
  },

  removeMember: async (projectId: string, userId: string): Promise<void> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/projects/${projectId}/team/${userId}`, {
      method: 'DELETE',
    });

    if (!response.ok) throw await responseError(response);
  },

  cancelInvitation: async (projectId: string, invitationId: string): Promise<void> => {
    const response = await fetchWithAuth(
      `${API_URL}/api/v1/projects/${projectId}/invitations/${invitationId}`,
      { method: 'DELETE' }
    );

    if (!response.ok) throw await responseError(response);
  },

  getInvitation: async (token: string): Promise<InvitationDetails> => {
    const response = await fetch(`${API_URL}/api/v1/invitations/${token}`);

    return handleJsonResponse(response, 'Invitation not found');
  },

  acceptInvitation: async (inviteToken: string): Promise<{ project_id: string }> => {
    const response = await fetchWithAuth(`${API_URL}/api/v1/invitations/${inviteToken}/accept`, {
      method: 'POST',
    });

    return handleJsonResponse(response, 'Failed to accept invitation');
  },
};
